import { useVariantPriceLayout } from './useVariantPriceLayout'
import {
  Button,
  GreyGrey30,
  Modal,
  Box,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { ReactNode } from 'react'
import { VariantPriceLayoutContent } from './VariantPriceLayoutContent'

export const VariantPriceLayout = (props: {
  loading: boolean
  variantPriceId?: string
  onCancel?: () => void
  drawerLeadingContent?: ReactNode
}) => {
  const {
    isListPriceBasedVariant,
    pricingModel,
    onCancel,
    submit,
    submitting,
    title
  } = useVariantPriceLayout({
    variantPriceId: props.variantPriceId,
    onCancel: props.onCancel
  })

  const submitDisabled = submitting || submit.disabled

  // The save navigates away (closing the drawer) itself on success via its
  // `afterSave` callback, so this doesn't need to close the drawer — matching
  // ListPriceLayout. A failed/invalid save leaves the drawer open.
  const handleSubmit = () => {
    void submit.action()
  }

  // The legacy SimpleAsyncModalUI submitted on Enter; preserve that here.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') {
      return
    }

    e.stopPropagation()

    if (submitDisabled) {
      return
    }

    handleSubmit()
  }

  return (
    <Modal
      isOpen
      variant="drawer"
      onKeyDown={handleKeyDown}
      onClose={onCancel}
      data-testid="priceEditor.variant"
      width={props.drawerLeadingContent ? '984px' : undefined}
    >
      <Grid
        gridTemplateAreas={
          props.drawerLeadingContent
            ? `"drawerLeadingContent mainContent"`
            : `"mainContent"`
        }
        gridTemplateColumns={props.drawerLeadingContent ? '1fr 1fr' : '1fr'}
        overflow="hidden"
        height="100%"
        width="100%"
      >
        {props.drawerLeadingContent && (
          <GridItem
            gridArea="drawerLeadingContent"
            borderRight={`${GreyGrey30} 1px solid`}
            minHeight="0"
          >
            {props.drawerLeadingContent}
          </GridItem>
        )}
        <GridItem gridArea="mainContent" minHeight="0">
          <Grid
            gridTemplateAreas={`
              "header"
              "content"
              "footer"
            `}
            gridTemplateRows="auto 1fr auto"
            overflow="hidden"
            height="100%"
            width="100%"
          >
            <GridItem gridArea="header">
              <Modal.Header showCloseButton withBorder>
                {title}
              </Modal.Header>
            </GridItem>
            <GridItem gridArea="content" overflow="auto">
              <Modal.Content padding={0}>
                <Grid
                  templateAreas={`"content"`}
                  templateRows={'1fr'}
                  width="100%"
                  height="100%"
                >
                  <GridItem gridArea="content">
                    <Box
                      width="100%"
                      minWidth="400px"
                      data-testid="price-form"
                      padding="24px"
                    >
                      <VariantPriceLayoutContent
                        loading={props.loading}
                        pricingModel={pricingModel}
                        isListPriceBasedVariant={isListPriceBasedVariant}
                      />
                    </Box>
                  </GridItem>
                </Grid>
              </Modal.Content>
            </GridItem>
            <GridItem gridArea="footer">
              <Modal.Footer withBorder>
                <Box flex={1}></Box>
                <Button
                  data-testid="priceEditor.variant.cancel"
                  variant="secondary"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="priceEditor.variant.submit"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={submitDisabled}
                  submitting={submitting}
                  submittingText={submit.submittingText}
                >
                  {submit.text}
                </Button>
              </Modal.Footer>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Modal>
  )
}
